import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, StatCard, Grid3, LoadingSpinner } from "../components/ui";
import { useAnalyticsOverview } from "../hooks/useAnalytics";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { Squares2X2Icon, ChartBarIcon } from "@heroicons/react/24/solid";

export default function Home() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { data: analytics, analyticsIsLoading } = useAnalyticsOverview();

  // Client-mode users always land on their portal, not the internal home page
  useEffect(() => {
    if (permissions?.clientMode && permissions?.clientCompanyId) {
      navigate(`/portal/${permissions.clientCompanyId}`, { replace: true });
    }
  }, [permissions, navigate]);

  const { theme } = useTheme();

  return (
    <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center">
      {/* Hero Section */}
      <div>
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo */}
            <div className="mb-8 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-brand-primary/30 dark:bg-brand-primary/20 blur-3xl rounded-full"></div>
              </div>
            </div>

            {/* Heading */}
            <h1 className="text-4xl md:text-6xl font-bold text-text-primary-light dark:text-text-primary mb-6">
              <span className="text-brand-primary inline-flex items-center gap-2">
                <img
                  src={theme === "light" ? "/logolight.png" : "/logo.png"}
                  alt="SeyAI Insights"
                  className="w-64"
                />
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-xl md:text-2xl text-text-secondary-light dark:text-text-secondary mb-4 max-w-2xl mx-auto">
              Benchmark your company's AI readiness against the industry
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/dashboard")}
              >
                <span className="flex items-center gap-2">
                  <Squares2X2Icon className="w-5 h-5" />
                  View Dashboard
                </span>
              </Button>

              <Button
                variant="secondary"
                size="lg"
                onClick={() => navigate("/insights")}
              >
                <span className="flex items-center gap-2">
                  <ChartBarIcon className="w-5 h-5" />
                  Explore Insights
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="container mx-auto px-4 py-8">
        {analyticsIsLoading ? (
          <div className="flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : analytics ? (
          <>
            <Grid3 className="mb-16 max-w-5xl mx-auto">
              <StatCard
                title="Companies"
                value={analytics.totalCompanies || 0}
                icon={
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                }
                trend="Organizations analyzed"
                variant="default"
              />

              <StatCard
                title="Total Responses"
                value={analytics.totalSurveyResponses?.toLocaleString() || 0}
                icon={
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                }
                trend="Survey participants"
                variant="default"
              />

              <StatCard
                title="Avg Adoption Rate"
                value={`${analytics.adoption.averageAdoptionRate?.toFixed(1) || 0}%`}
                icon={
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                }
                trend="AI adoption across companies"
                variant="default"
              />
            </Grid3>
          </>
        ) : null}
      </div>
    </div>
  );
}
